import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../hooks/useApi';

const S = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 9000,
    background: 'rgba(15,23,42,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 16,
  },
  modal: {
    background: '#ffffff',
    borderRadius: 12,
    width: '100%',
    maxWidth: 1100,
    maxHeight: '92vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(15,23,42,0.25)',
    overflow: 'hidden',
  },
  header: {
    padding: '14px 20px',
    borderBottom: '1px solid #dbe4ef',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  title: { fontSize: 16, fontWeight: 700, color: '#0f172a' },
  subtitle: { fontSize: 12, color: '#64748b' },
  btn: {
    padding: '7px 14px',
    background: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    cursor: 'pointer',
    fontWeight: 600,
  },
  btnGhost: {
    padding: '7px 14px',
    background: 'transparent',
    color: '#334155',
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    fontSize: 13,
    cursor: 'pointer',
  },
  body: { padding: 18, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 14 },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 },
  statCard: {
    background: '#f8fafc',
    border: '1px solid #dbe4ef',
    borderRadius: 8,
    padding: '10px 12px',
    textAlign: 'center',
  },
  statNum: { fontSize: 22, fontWeight: 800, color: '#0f172a' },
  statLbl: { fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 4 },
  block: { border: '1px solid #dbe4ef', borderRadius: 8, padding: 12, background: '#ffffff' },
  blockTitle: { fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 8 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #dbe4ef', color: '#64748b', fontWeight: 600 },
  td: { padding: '6px 8px', borderBottom: '1px solid #f1f5f9', color: '#0f172a', verticalAlign: 'top' },
  badge: (color, bg) => ({
    display: 'inline-block',
    fontSize: 10,
    padding: '2px 6px',
    borderRadius: 4,
    fontWeight: 700,
    color,
    background: bg,
    border: `1px solid ${color}33`,
  }),
};

function formatarDataHora(valor) {
  if (!valor) return '-';
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('pt-BR');
}

export default function BackupsViewer({ token, onClose }) {
  const api = useApi(token);
  const [carregando, setCarregando] = useState(true);
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [filtroBase, setFiltroBase] = useState('');

  const carregar = useCallback(async ({ atualizar = false } = {}) => {
    try {
      setCarregando(true);
      setErro('');
      const resp = await api.getBackupsStatus({ atualizar });
      setDados(resp);
    } catch (err) {
      setErro(err.message || 'Erro ao consultar backups');
    } finally {
      setCarregando(false);
    }
  }, [api]);

  useEffect(() => { carregar(); }, [carregar]);

  const basesFiltradas = useMemo(() => {
    if (!dados?.byDatabase) return [];
    const termo = filtroBase.trim().toLowerCase();
    if (!termo) return dados.byDatabase;
    return dados.byDatabase.filter((d) => d.base.includes(termo));
  }, [dados, filtroBase]);

  const summary = dados?.summary || {};
  const issues = dados?.issues || {};
  const statusOk = dados?.status === 'ok';

  return (
    <div style={S.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={S.modal}>
        <div style={S.header}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={S.title}>Backups Banco de Dados</div>
            <div style={S.subtitle}>
              Dia: {dados?.date || '...'} &middot; pasta: {dados?.folder || '...'}
              {dados?.fromCache ? ' (de cache)' : ''}
              {dados?.checkedAt ? ` &middot; verificado em ${formatarDataHora(dados.checkedAt)}` : ''}
            </div>
          </div>
          <button style={S.btnGhost} onClick={() => carregar({ atualizar: true })} disabled={carregando}>
            {carregando ? 'Atualizando...' : 'Reescanear'}
          </button>
          <button style={S.btnGhost} onClick={onClose}>Fechar</button>
        </div>

        <div style={S.body}>
          {erro && (
            <div style={{ ...S.block, borderColor: '#fecaca', background: '#fef2f2', color: '#991b1b' }}>
              {erro}
            </div>
          )}

          {dados && (
            <>
              <div style={{
                ...S.block,
                background: statusOk ? '#f0fdf4' : '#fef2f2',
                borderColor: statusOk ? '#bbf7d0' : '#fecaca',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={S.badge(
                    statusOk ? '#15803d' : '#b91c1c',
                    statusOk ? '#dcfce7' : '#fee2e2',
                  )}>
                    {statusOk ? 'OK' : 'INCONSISTENTE'}
                  </span>
                  <span style={{ fontSize: 13, color: '#0f172a' }}>
                    {statusOk
                      ? 'Todas as bases auditadas tem FULL e os DIFFs estao alinhados.'
                      : 'Foram detectadas inconsistencias - veja abaixo.'}
                  </span>
                </div>
              </div>

              <div style={S.statsRow}>
                <div style={S.statCard}>
                  <div style={S.statNum}>{summary.basesNoSql}</div>
                  <div style={S.statLbl}>Bases online no SQL</div>
                </div>
                <div style={S.statCard}>
                  <div style={{ ...S.statNum, color: summary.basesOffline ? '#b45309' : '#0f172a' }}>
                    {summary.basesOffline || 0}
                  </div>
                  <div style={S.statLbl}>Bases offline</div>
                </div>
                <div style={S.statCard}>
                  <div style={{ ...S.statNum, color: '#16a34a' }}>{summary.basesComFull}</div>
                  <div style={S.statLbl}>Com FULL hoje</div>
                </div>
                <div style={S.statCard}>
                  <div style={{ ...S.statNum, color: summary.basesSemFull ? '#b91c1c' : '#16a34a' }}>
                    {summary.basesSemFull}
                  </div>
                  <div style={S.statLbl}>Sem FULL hoje</div>
                </div>
                <div style={S.statCard}>
                  <div style={S.statNum}>{summary.diffReferencia}</div>
                  <div style={S.statLbl}>DIFF referencia</div>
                </div>
                <div style={S.statCard}>
                  <div style={{ ...S.statNum, color: summary.basesDiffDivergente ? '#b91c1c' : '#16a34a' }}>
                    {summary.basesDiffDivergente}
                  </div>
                  <div style={S.statLbl}>Bases c/ DIFF divergente</div>
                </div>
                <div style={S.statCard}>
                  <div style={S.statNum}>{summary.arquivosTotal}</div>
                  <div style={S.statLbl}>Arquivos no dia</div>
                </div>
              </div>

              {issues.sqlError && (
                <div style={{ ...S.block, background: '#fffbeb', borderColor: '#fcd34d' }}>
                  <div style={S.blockTitle}>Erro ao consultar SQL</div>
                  <div style={{ fontSize: 12, color: '#92400e' }}>{issues.sqlError}</div>
                </div>
              )}

              {issues.pastaInexistente && (
                <div style={{ ...S.block, background: '#fef2f2', borderColor: '#fecaca' }}>
                  <div style={S.blockTitle}>Pasta nao encontrada</div>
                  <div style={{ fontSize: 12, color: '#991b1b' }}>{issues.pastaInexistente}</div>
                </div>
              )}

              {(dados.databasesOffline || []).length > 0 && (
                <div style={{ ...S.block, background: '#fffbeb', borderColor: '#fcd34d' }}>
                  <div style={{ ...S.blockTitle, color: '#b45309' }}>
                    Bases fora do ar - nao auditadas ({dados.databasesOffline.length})
                  </div>
                  <div style={{ fontSize: 11, color: '#92400e', marginBottom: 8 }}>
                    Estas bases nao estao ONLINE no SQL Server, entao nao entram na
                    verificacao de backup nem afetam o status.
                  </div>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Base</th>
                        <th style={S.th}>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dados.databasesOffline.map((d) => (
                        <tr key={d.base}>
                          <td style={S.td}>{d.base}</td>
                          <td style={S.td}>
                            <span style={S.badge('#b45309', '#fef3c7')}>{d.estado}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {(issues.basesSemFull || []).length > 0 && (
                <div style={S.block}>
                  <div style={{ ...S.blockTitle, color: '#b91c1c' }}>
                    Bases SEM backup FULL hoje ({issues.basesSemFull.length})
                  </div>
                  <div style={{ fontSize: 12, color: '#0f172a', whiteSpace: 'pre-wrap' }}>
                    {issues.basesSemFull.join(', ')}
                  </div>
                </div>
              )}

              {(issues.basesDiffDivergente || []).length > 0 && (
                <div style={S.block}>
                  <div style={{ ...S.blockTitle, color: '#b45309' }}>
                    Bases com numero de DIFFs divergente (esperado: {summary.diffReferencia})
                  </div>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Base</th>
                        <th style={S.th}>DIFFs encontrados</th>
                      </tr>
                    </thead>
                    <tbody>
                      {issues.basesDiffDivergente.map((item) => (
                        <tr key={item.base}>
                          <td style={S.td}>{item.base}</td>
                          <td style={S.td}>{item.diffCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {(issues.basesExtras || []).length > 0 && (
                <div style={S.block}>
                  <div style={{ ...S.blockTitle, color: '#b45309' }}>
                    Arquivos de bases que nao estao no SQL ({issues.basesExtras.length})
                  </div>
                  <div style={{ fontSize: 12, color: '#0f172a' }}>
                    {issues.basesExtras.join(', ')}
                  </div>
                </div>
              )}

              <div style={S.block}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  marginBottom: 8, gap: 10, flexWrap: 'wrap',
                }}>
                  <div style={S.blockTitle}>Espelho por base ({basesFiltradas.length})</div>
                  <input
                    type="text"
                    value={filtroBase}
                    onChange={(e) => setFiltroBase(e.target.value)}
                    placeholder="Filtrar base..."
                    style={{
                      padding: '6px 10px',
                      border: '1px solid #cbd5e1',
                      borderRadius: 6,
                      fontSize: 12,
                      minWidth: 200,
                    }}
                  />
                </div>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Base</th>
                      <th style={S.th}>FULL</th>
                      <th style={S.th}>DIFF</th>
                      <th style={S.th}>Primeiro FULL</th>
                      <th style={S.th}>Ultimo FULL</th>
                      <th style={S.th}>Ultimo DIFF</th>
                      <th style={S.th}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {basesFiltradas.map((d) => {
                      const semFull = d.fullCount === 0;
                      const diffDiv = summary.diffReferencia > 0 && d.diffCount > 0 && d.diffCount !== summary.diffReferencia;
                      const status = !d.esperada
                        ? { txt: 'Nao esta no SQL', cor: '#b45309', bg: '#fffbeb' }
                        : semFull
                          ? { txt: 'Sem FULL', cor: '#b91c1c', bg: '#fee2e2' }
                          : diffDiv
                            ? { txt: 'DIFF divergente', cor: '#b45309', bg: '#fef3c7' }
                            : { txt: 'OK', cor: '#15803d', bg: '#dcfce7' };
                      return (
                        <tr key={d.base}>
                          <td style={S.td}>{d.base}</td>
                          <td style={S.td}>{d.fullCount}</td>
                          <td style={S.td}>{d.diffCount}</td>
                          <td style={S.td}>{d.primeiroFullHora || '-'}</td>
                          <td style={S.td}>{d.ultimoFullHora || '-'}</td>
                          <td style={S.td}>{d.ultimoDiffHora || '-'}</td>
                          <td style={S.td}>
                            <span style={S.badge(status.cor, status.bg)}>{status.txt}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {!dados && carregando && (
            <div style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>Carregando...</div>
          )}
        </div>
      </div>
    </div>
  );
}
